import { IsEmail, IsNotEmpty } from 'class-validator';
import z from 'zod';

export type DoNotContactDto = {
  id: number;
  reason: string;
  updatedAt: number;
  createdAt: number;
};

export interface PhoneNumberDto {
  id: number;
  number?: string;
  primary?: boolean;
  description?: string;
  type: string;
  carrier: string;
  src: string;
  interactableId: string;
  createdAt: number;
  updatedAt?: number;
  doNotContact: DoNotContactDto;
  /**
   * Distinct outbound calls whose Twilio leg to this number was `failed`,
   * after the latest incoming call from the number. Omitted when call
   * history was not loaded. Two or more means the number is invalid.
   */
  failedAttemptCount?: number;
}

export interface EmailAddressDto {
  id: number;
  email: string;
  primary?: boolean;
  description: string;
  source: string;
  interactableId: string;
}

export class EmailAddressFormSubmissionDto {
  constructor(data?: Partial<EmailAddressFormSubmissionDto>) {
    Object.assign(this, data);
  }
  emailAddressId: number;

  @IsNotEmpty({ message: 'Email is required' })
  @IsEmail({}, { message: 'Email is not valid' })
  emailAddress: string;

  @IsNotEmpty({ message: 'Description is required' })
  description: string;
}

export interface AddressDto {
  id: number;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
  primary?: boolean;
  description?: string;
  src: string;
  interactableId: string;
  lat: number;
  lng: number;
  createdAt: number;
  updatedAt?: number;
  deletedAt?: number;
}

export interface CoordsDto {
  lat: number;
  lng: number;
}

export interface InteractableDeleteResponseDto {
  id: number;
}

export const createAddressSchema = z.object({
  userId: z.string(),
  addressId: z.number().nullable().optional(),
  description: z.string().min(1, 'Address Label is required'),
  line1: z.string().min(1, 'Address line 1 is required'),
  line2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  zip: z.string().min(5, 'ZIP code must be at least 5 characters'),
  primary: z.boolean(),
});

export const updateAddressSchema = createAddressSchema.partial();

export type CreateAddressForm = z.infer<typeof createAddressSchema>;
export type UpdateAddressForm = z.infer<typeof updateAddressSchema>;

export const createEmailAddressSchema = z.object({
  emailAddress: z.string().email(),
  description: z.string().min(1, 'Description is required'),
});

export const updateEmailAddressSchema = z.object({
  emailAddress: z.string().email().optional(),
  description: z.string().min(1, 'Description is required').optional(),
  primary: z.boolean().optional(),
  emailAddressId: z.number().optional(),
});

export type CreateEmailAddressForm = z.infer<typeof createEmailAddressSchema>;
export type UpdateEmailAddressForm = z.infer<typeof updateEmailAddressSchema>;
